import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Container,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useParams } from "react-router-dom";
import { DeletedRecord } from "../lib/types";
import { deletedRecordsAPI } from "../services/api";

const formatLabel = (record: DeletedRecord) => {
  const metadata = record.recordMetadata || {};
  const nameCandidates = [
    metadata.name,
    metadata.account,
    metadata.vendor,
    metadata.creditor,
    metadata.client,
    metadata.description,
  ];
  const label =
    nameCandidates.find((value) => typeof value === "string" && value.trim()) ??
    null;
  return label ? `${record.recordType}: ${label}` : record.recordType;
};

const GroupDeletedRecordsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [records, setRecords] = useState<DeletedRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    const loadDeletedRecords = async () => {
      try {
        setLoading(true);
        const allRecords = await deletedRecordsAPI.list();
        setRecords(allRecords);
      } catch (error) {
        console.error("Failed to load deleted records:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDeletedRecords();
  }, []);

  const filteredRecords = useMemo(() => {
    if (!id) {
      return records;
    }
    return records.filter((record) => record.groupId === id);
  }, [id, records]);

  const handleRestore = async (recordId: string) => {
    try {
      console.log("Restoring deleted record", { recordId });
      setRestoringId(recordId);
      await deletedRecordsAPI.restore(recordId);
      setRecords((prev) => prev.filter((record) => record.id !== recordId));
      setNotice({ type: "success", message: "Record restored." });
    } catch (error) {
      console.error("Failed to restore record:", error);
      setNotice({ type: "error", message: "Restore failed." });
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h4" fontWeight={800}>
          Deleted records
        </Typography>
        <Typography color="text.secondary">
          Restoring a record reopens it for access but does not recreate related
          data automatically.
        </Typography>
        <Card elevation={0} sx={{ borderRadius: 1 }}>
          <CardContent>
            {loading ? (
              <Typography color="text.secondary">
                Loading deleted records...
              </Typography>
            ) : filteredRecords.length === 0 ? (
              <Typography color="text.secondary">
                No deleted records yet.
              </Typography>
            ) : (
              <List>
                {filteredRecords.map((record) => {
                  const restored = Boolean(record.restoredAt);
                  return (
                    <ListItem
                      key={record.id}
                      divider
                      secondaryAction={
                        restored ? (
                          <Tooltip title="Restored">
                            <span>
                              <IconButton disabled>
                                <CheckCircleIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : restoringId === record.id ? (
                          <Tooltip title="Restoring">
                            <span>
                              <IconButton disabled>
                                <CircularProgress size={16} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Restore">
                            <IconButton
                              onClick={() => handleRestore(record.id)}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                        )
                      }
                    >
                      <ListItemText
                        primary={formatLabel(record)}
                        secondary={`Deleted ${new Date(
                          record.deletedAt
                        ).toLocaleString()}`}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>
      </Stack>
      {notice && (
        <Snackbar
          open
          autoHideDuration={4000}
          onClose={() => setNotice(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert
            onClose={() => setNotice(null)}
            severity={notice.type}
            variant="filled"
          >
            {notice.message}
          </Alert>
        </Snackbar>
      )}
    </Container>
  );
};

export default GroupDeletedRecordsPage;
